import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Users, UserPlus, Trash2, Loader2, ShieldCheck, Coins, Mail, Clock, Settings2, Check } from "lucide-react";

/**
 * Team management panel — invite members, remove them, buy extra seats.
 * Renders inline inside the Settings page.
 */
export function TeamPanel() {
    const [data, setData] = useState(null);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviting, setInviting] = useState(false);
    const [buyingSeat, setBuyingSeat] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);   // membership_id being edited
    const [projects, setProjects] = useState([]);
    const [editRole, setEditRole] = useState("member");
    const [editAllowedIds, setEditAllowedIds] = useState(null); // null = all
    const [savingAccess, setSavingAccess] = useState(false);

    const load = () => {
        client.get("/team").then((r) => setData(r.data)).catch(() => { /* handled by nag */ }).finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        client.get("/projects").then((r) => setProjects(r.data || [])).catch(() => { /* ignore */ });
    }, []);

    const openEdit = (m) => {
        setEditing(m.id);
        setEditRole(m.role || "member");
        setEditAllowedIds(m.allowed_project_ids ?? null);
    };

    const toggleProject = (pid) => {
        setEditAllowedIds((prev) => {
            const cur = prev === null ? projects.map((p) => p.id) : prev;
            return cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid];
        });
    };

    const saveAccess = async () => {
        setSavingAccess(true);
        try {
            await client.patch(`/team/members/${editing}`, {
                role: editRole,
                allowed_project_ids: editAllowedIds,
            });
            toast.success("Access updated");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        } finally {
            setSavingAccess(false);
        }
    };

    const invite = async (e) => {
        e.preventDefault();
        if (!inviteEmail.trim()) return;
        setInviting(true);
        try {
            await client.post("/team/invite", { email: inviteEmail.trim() });
            toast.success(`Invite sent to ${inviteEmail}`);
            setInviteEmail("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invite failed");
        } finally {
            setInviting(false);
        }
    };

    const remove = async (m) => {
        if (!confirm(`Remove ${m.invited_email || m.email} from the team?`)) return;
        try {
            await client.delete(`/team/members/${m.id}`);
            toast.success("Removed");
            load();
        } catch { toast.error("Remove failed"); }
    };

    const buySeat = async (lookup_key) => {
        setBuyingSeat(lookup_key);
        try {
            const r = await client.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
            if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Checkout failed");
        } finally {
            setBuyingSeat(null);
        }
    };

    if (loading) return <div className="text-muted-foreground font-mono text-sm">loading team…</div>;
    if (!data) return null;

    const s = data.seats;
    const isPaid = s.tier === "pro" || s.tier === "agency";

    return (
        <div className="border border-border bg-card/40 p-6" data-testid="team-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="overline flex items-center gap-2"><Users className="w-3 h-3" /> TEAM</div>
                    <div className="font-display text-xl mt-1">Bring your team along.</div>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Invite teammates to share projects, keywords, and AI credits. Each seat gets full access — no separate billing.
                    </p>
                </div>
                <div className="border border-primary/40 bg-primary/5 px-4 py-2 text-right">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Seats</div>
                    <div className="font-mono text-2xl text-primary" data-testid="seat-count">
                        {s.used}<span className="text-sm text-muted-foreground">/{s.total}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                        {s.included} incl · {s.extra > 0 ? `+${s.extra} extra` : "no extras"}
                    </div>
                </div>
            </div>

            {!isPaid ? (
                <div className="mt-6 border border-warning/40 bg-warning/5 p-4 text-sm" data-testid="team-locked">
                    Team collaboration is a <span className="text-warning font-medium">Pro or Agency</span> feature.{" "}
                    <Link to="/pricing" className="text-primary hover:underline">Upgrade to invite teammates →</Link>
                </div>
            ) : (
                <>
                    {/* Members list */}
                    <div className="mt-6">
                        <div className="overline mb-2 text-xs">MEMBERS ({1 + data.members.length}/{s.total})</div>
                        <div className="border border-border divide-y divide-border">
                            {/* Owner row */}
                            <div className="p-3 flex items-center gap-3 text-sm">
                                <div className="w-8 h-8 border border-primary/40 bg-primary/10 text-primary flex items-center justify-center text-xs font-mono">
                                    {(data.owner.name || data.owner.email || "?").slice(0, 1).toUpperCase()}
                                </div>
                                <div className="flex-1">
                                    <div className="font-mono">{data.owner.name || data.owner.email}</div>
                                    <div className="text-xs text-muted-foreground">{data.owner.email}</div>
                                </div>
                                <span className="text-[10px] font-mono uppercase tracking-widest border border-primary/40 text-primary px-2 py-0.5">OWNER</span>
                            </div>
                            {data.members.map((m) => (
                                <div key={m.id} data-testid={`team-member-${m.id}`}>
                                    <div className="p-3 flex items-center gap-3 text-sm">
                                        <div className="w-8 h-8 border border-border bg-card/40 flex items-center justify-center text-xs font-mono text-muted-foreground">
                                            {(m.invited_email || "?").slice(0, 1).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-mono truncate">{m.invited_email}</div>
                                            <div className="text-xs text-muted-foreground">
                                                Joined {m.accepted_at ? new Date(m.accepted_at).toLocaleDateString() : "—"}
                                                {m.allowed_project_ids !== null && m.allowed_project_ids !== undefined && m.role !== "admin" && (
                                                    <span className="ml-2 text-warning">· {m.allowed_project_ids.length} project{m.allowed_project_ids.length === 1 ? "" : "s"}</span>
                                                )}
                                            </div>
                                        </div>
                                        <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${m.role === "admin" ? "border-primary/40 text-primary" : "border-border text-muted-foreground"}`}>{m.role}</span>
                                        <button
                                            onClick={() => openEdit(m)}
                                            className="text-muted-foreground hover:text-primary p-1"
                                            title="Edit role + project access"
                                            data-testid={`edit-member-${m.id}`}
                                        >
                                            <Settings2 className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => remove(m)} className="text-muted-foreground hover:text-destructive p-1" title="Remove">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    {editing === m.id && (
                                        <div className="border-t border-border/60 bg-card/60 p-4 space-y-4" data-testid="edit-access-panel">
                                            <div>
                                                <div className="text-[10px] text-muted-foreground uppercase tracking-widest mb-2">Role</div>
                                                <div className="flex gap-2">
                                                    {["member", "admin"].map((r) => (
                                                        <button
                                                            key={r}
                                                            data-testid={`role-${r}`}
                                                            onClick={() => setEditRole(r)}
                                                            className={`text-xs px-3 py-1.5 border ${editRole === r ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"}`}
                                                        >
                                                            {r === "admin" ? "Admin · full access + billing" : "Member · restricted"}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            {editRole === "member" && (
                                                <div>
                                                    <div className="text-[10px] text-muted-foreground uppercase tracking-widest mb-2 flex items-center justify-between">
                                                        <span>Project access</span>
                                                        <label className="flex items-center gap-1.5 cursor-pointer normal-case tracking-normal">
                                                            <input
                                                                type="checkbox"
                                                                checked={editAllowedIds === null}
                                                                onChange={(e) => setEditAllowedIds(e.target.checked ? null : (projects.map((p) => p.id)))}
                                                                className="accent-primary"
                                                                data-testid="allow-all-projects"
                                                            />
                                                            <span className="text-xs text-muted-foreground">All projects</span>
                                                        </label>
                                                    </div>
                                                    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-border p-2 ${editAllowedIds === null ? "opacity-50 pointer-events-none" : ""}`}>
                                                        {projects.length === 0 && <div className="text-xs text-muted-foreground p-2">No projects yet</div>}
                                                        {projects.map((p) => {
                                                            const active = editAllowedIds === null || editAllowedIds.includes(p.id);
                                                            return (
                                                                <label key={p.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-accent/30 px-2 py-1">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={active}
                                                                        onChange={() => toggleProject(p.id)}
                                                                        className="accent-primary"
                                                                        data-testid={`project-toggle-${p.id}`}
                                                                    />
                                                                    <span className="font-mono truncate">{p.name} <span className="text-muted-foreground">· {p.domain}</span></span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="flex gap-2">
                                                <button
                                                    data-testid="save-access"
                                                    onClick={saveAccess}
                                                    disabled={savingAccess}
                                                    className="bg-primary text-primary-foreground px-3 py-1.5 text-xs font-medium hover:bg-primary/90 flex items-center gap-1 disabled:opacity-50"
                                                >
                                                    {savingAccess ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => setEditing(null)}
                                                    className="border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {data.invites.map((inv) => (
                                <div key={inv.id} className="p-3 flex items-center gap-3 text-sm bg-warning/5" data-testid={`team-invite-${inv.id}`}>
                                    <div className="w-8 h-8 border border-warning/40 bg-warning/5 text-warning flex items-center justify-center">
                                        <Clock className="w-3.5 h-3.5" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-mono">{inv.invited_email}</div>
                                        <div className="text-xs text-warning">
                                            Pending · sent {new Date(inv.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <span className="text-[10px] font-mono uppercase tracking-widest border border-warning/40 text-warning px-2 py-0.5">INVITED</span>
                                    <button onClick={() => remove(inv)} className="text-muted-foreground hover:text-destructive p-1" title="Revoke">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Invite form */}
                    <form onSubmit={invite} className="mt-6 flex gap-2 flex-wrap" data-testid="invite-form">
                        <div className="relative flex-1 min-w-[240px]">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <input
                                data-testid="invite-email"
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="teammate@example.com"
                                className="w-full bg-input border border-border pl-10 pr-3 py-2 text-sm focus:outline-none focus:border-primary"
                            />
                        </div>
                        <button
                            data-testid="invite-submit"
                            type="submit"
                            disabled={inviting || s.available <= 0}
                            title={s.available <= 0 ? "Add an extra seat to invite more" : ""}
                            className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
                        >
                            {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                            Invite
                        </button>
                    </form>
                    {s.available <= 0 && (
                        <div className="text-xs text-warning mt-2" data-testid="seat-full-warning">
                            All {s.total} seats used. Add an extra seat below to keep growing your team.
                        </div>
                    )}

                    {/* Buy extra seats */}
                    <div className="mt-6 border border-primary/30 bg-primary/5 p-4">
                        <div className="overline flex items-center gap-2 mb-2 text-primary">
                            <ShieldCheck className="w-3 h-3" /> ADD EXTRA SEAT
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">
                            Each extra seat = one more teammate. Cancel anytime from Stripe.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            {data.seat_products.map((p) => (
                                <button
                                    key={p.lookup_key}
                                    data-testid={`buy-seat-${p.lookup_key}`}
                                    onClick={() => buySeat(p.lookup_key)}
                                    disabled={buyingSeat !== null}
                                    className="border border-primary/60 text-primary hover:bg-primary/10 py-2.5 px-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {buyingSeat === p.lookup_key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Coins className="w-3.5 h-3.5" />}
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
